import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Download, Share2, Trash2 } from "lucide-react";
import { LIMITS, normalizeListKind, type ListKind } from "@checklist/shared";
import { ImportItemsModal } from "../components/ImportItemsModal.js";
import {
  ErrorBanner,
  fieldCls,
  inlineFieldCls,
  labelCls,
} from "../components/AuthShell.js";
import {
  DndContext,
  DragHandle,
  SortableList,
  SortableRow,
  arrayMove,
  closestCenter,
  useDndSensors,
  type DragEndEvent,
} from "../components/SortableList.js";
import { Button } from "../components/ui/Button.js";
import { useConfirm } from "../components/ui/ConfirmDialog.js";
import { CharCount } from "../components/ui/CharCount.js";
import { DescriptionHint } from "../components/ui/DescriptionHint.js";
import { ItemSortButtons } from "../components/ui/ItemSortButtons.js";
import { CopyItemsButton } from "../components/ui/CopyItemsButton.js";
import { Modal } from "../components/ui/Modal.js";
import { RemoveRowButton } from "../components/ui/RemoveRowButton.js";
import { PdfExportDialog } from "../components/PdfExportDialog.js";
import { ResourceNotFound } from "../components/ResourceNotFound.js";
import { SharingPanel } from "../components/SharingPanel.js";
import { showErrorToast, showSuccessToast } from "../components/ui/Toast.js";
import { inheritedTemplateKind } from "../lib/templateKind.js";
import { suggestedChecklistName } from "../lib/checklistName.js";
import {
  ApiError,
  createTemplate,
  deleteTemplate,
  fetchLists,
  fetchList,
  fetchTemplate,
  instantiateTemplate,
  resolveStoredShareRole,
  updateTemplate,
  type ApiList,
  type ApiListSummary,
  type ApiTemplate,
  type ApiTemplateSection,
} from "../lib/api.js";
import { humanizeApiError } from "../lib/apiErrors.js";
import { describeCreateWarnings, isOrphanedListSection } from "../lib/createWarnings.js";
import { importResultMessage, selectImportItems } from "../lib/importParser.js";
import { templateToPdfDoc } from "../lib/pdf/adapters.js";
import { sortItemsByText, type SortDir } from "../lib/sortItems.js";
import { useAuth } from "../context/AuthContext.js";
import { useResourceLive } from "../hooks/useResourceLive.js";
import { DraftRestoreBar } from "../components/DraftRestoreBar.js";
import {
  GuardedLink,
  useDraftMirror,
  useRegisterUnsaved,
} from "../components/UnsavedChangesProvider.js";
import { clearDraft, draftKey, loadDraft } from "../lib/draftStorage.js";
import { diffCount } from "../lib/unsavedChanges.js";
import { formatPageTitle } from "../lib/pageTitle.js";

function kindToggleCls(active: boolean): string {
  return [
    "h-9 rounded-lg border px-3 text-sm font-semibold transition",
    active
      ? "border-brand-600 bg-brand-600 text-white"
      : "border-line bg-white text-ink hover:bg-slate-50",
  ].join(" ");
}

type DraftListSection = { key: string; type: "list"; listId: string; name: string };
type DraftInlineSection = {
  key: string;
  type: "inline";
  name: string;
  items: Array<{ id: string; text: string }>;
};
type DraftSection = DraftListSection | DraftInlineSection;

function newKey(): string {
  return crypto.randomUUID();
}

type TemplateDraft = {
  name: string;
  description: string;
  tagsText: string;
  kind: ListKind;
  sections: ApiTemplateSection[];
};

const EMPTY_TEMPLATE_DRAFT: TemplateDraft = {
  name: "",
  description: "",
  tagsText: "",
  kind: "packing",
  sections: [],
};

function isTemplateDraft(value: unknown): value is TemplateDraft {
  return (
    !!value &&
    typeof value === "object" &&
    "name" in value &&
    "sections" in value &&
    Array.isArray((value as TemplateDraft).sections)
  );
}

function sectionsToDraft(sections: ApiTemplateSection[]): DraftSection[] {
  return sections.map((s): DraftSection => {
    if (s.type === "list") {
      return { key: newKey(), type: "list", listId: s.listId, name: s.name };
    }
    return {
      key: newKey(),
      type: "inline",
      name: s.name,
      items: s.items.map((it) => ({ ...it })),
    };
  });
}

function toWriteSections(sections: DraftSection[]): ApiTemplateSection[] {
  return sections.map((s) => {
    if (s.type === "list") return { type: "list", listId: s.listId, name: s.name };
    return {
      type: "inline",
      name: s.name,
      items: s.items.map((it) => ({ id: it.id, text: it.text })),
    };
  });
}

export function TemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const confirm = useConfirm();
  const formId = useId();
  const { user } = useAuth();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [kind, setKind] = useState<ListKind>("packing");
  const kindTouchedRef = useRef(false);
  const [sections, setSections] = useState<DraftSection[]>([]);
  const [lists, setLists] = useState<ApiListSummary[]>([]);
  const [pdfLists, setPdfLists] = useState<ApiList[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    if (notFound) return;
    const title = name.trim() || (isNew ? "New template" : "Template");
    document.title = formatPageTitle(title);
  }, [isNew, name, notFound]);
  useEffect(() => {
    if (!notFound) return;
    document.title = formatPageTitle("Template not found");
  }, [notFound]);
  const [instantiating, setInstantiating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState("");
  const [version, setVersion] = useState(0);
  const [memberCount, setMemberCount] = useState(0);
  const [activeShareCount, setActiveShareCount] = useState(0);
  const [shareRole, setShareRole] = useState<"read" | "write" | null>(null);
  const savedDraftRef = useRef<string | null>(null);
  const currentDraftRef = useRef("");
  const [savedValue, setSavedValue] = useState<TemplateDraft>(EMPTY_TEMPLATE_DRAFT);
  const [pendingDraft, setPendingDraft] = useState<TemplateDraft | null>(null);
  const sensors = useDndSensors();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFilter, setPickerFilter] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");

  function draftSnapshot(
    value = { name, description, tagsText, kind, sections: toWriteSections(sections) }
  ): string {
    return JSON.stringify(value);
  }
  currentDraftRef.current = draftSnapshot();

  const draftValue = useMemo<TemplateDraft>(
    () => ({ name, description, tagsText, kind, sections: toWriteSections(sections) }),
    [name, description, tagsText, kind, sections]
  );
  const unsavedCount = diffCount(
    savedValue as unknown as Record<string, unknown>,
    draftValue as unknown as Record<string, unknown>
  );
  const mirrorKey = draftKey("template", !isNew && id ? id : "new");
  useRegisterUnsaved(`template:${!isNew && id ? id : "new"}`, unsavedCount);
  const draftMirror = useDraftMirror(mirrorKey, draftValue, unsavedCount > 0 && !loading);

  const restoreCheckedRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading) return;
    if (restoreCheckedRef.current === mirrorKey) return;
    restoreCheckedRef.current = mirrorKey;
    const stored = loadDraft(mirrorKey);
    if (!isTemplateDraft(stored)) return;
    if (
      diffCount(
        savedValue as unknown as Record<string, unknown>,
        stored as unknown as Record<string, unknown>
      ) === 0
    ) {
      clearDraft(mirrorKey);
      return;
    }
    setPendingDraft(stored);
  }, [loading, mirrorKey, savedValue]);

  function applyDraftValue(value: TemplateDraft) {
    setName(value.name);
    setDescription(value.description);
    setTagsText(value.tagsText);
    setKind(value.kind);
    setSections(sectionsToDraft(value.sections));
  }

  function parseTags(): string[] {
    return tagsText
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }

  function applyTemplateState(tmpl: ApiTemplate) {
    const nextSections = sectionsToDraft(tmpl.sections);
    const savedObj: TemplateDraft = {
      name: tmpl.name,
      description: tmpl.description ?? "",
      tagsText: (tmpl.tags ?? []).join(", "),
      kind: normalizeListKind(tmpl.kind),
      sections: toWriteSections(nextSections),
    };
    savedDraftRef.current = JSON.stringify(savedObj);
    setSavedValue(savedObj);
    setName(tmpl.name);
    setDescription(tmpl.description ?? "");
    setTagsText((tmpl.tags ?? []).join(", "));
    setKind(normalizeListKind(tmpl.kind));
    setOwnerId(tmpl.ownerId);
    setVersion(tmpl.version ?? 0);
    setMemberCount(tmpl.memberCount ?? 0);
    setActiveShareCount(tmpl.activeShareCount ?? 0);
    setSections(nextSections);
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        if (user) {
          const listData = await fetchLists();
          if (cancelled) return;
          setLists(listData);
        }

        if (isNew) {
          setLoading(false);
          return;
        }

        const tmpl = await fetchTemplate(id!);
        if (cancelled) return;
        applyTemplateState(tmpl);
        const role = await resolveStoredShareRole("template", id!);
        if (!cancelled && role) setShareRole(role);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403 && !user) {
          navigate(`/request-access/template/${id!}`, { replace: true });
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        setError(humanizeApiError(err, "Failed to load template"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate, user]);

  useEffect(() => {
    if (isNew || !id) return;
    function refetchIfClean() {
      if (savedDraftRef.current !== null && currentDraftRef.current !== savedDraftRef.current) {
        return;
      }
      void fetchTemplate(id!).then(applyTemplateState).catch(() => {
        // The initial loader owns visible errors; focus refetch is best-effort.
      });
    }
    function onVisibility() {
      if (document.visibilityState === "visible") refetchIfClean();
    }
    window.addEventListener("focus", refetchIfClean);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", refetchIfClean);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [id, isNew]);

  const isOwner = Boolean(user && ownerId && user.sub === ownerId);
  const canWrite = isOwner || shareRole === "write";
  const readOnly = !isNew && !canWrite;

  function reportActionError(err: unknown, fallback: string) {
    showErrorToast(humanizeApiError(err, fallback));
    if (err instanceof ApiError && err.code === "STALE_VERSION") {
      setError("Someone else changed this — reload to see their changes.");
    }
  }

  useResourceLive(
    "template",
    isNew ? undefined : id,
    { memberCount, activeShareCount },
    {
      onResource: (message) => {
        if (message.version <= version) return;
        if (savedDraftRef.current !== null && draftSnapshot() !== savedDraftRef.current) {
          const stale = "Someone else changed this — reload to see their changes.";
          showErrorToast(stale);
          setError(stale);
          return;
        }
        void fetchTemplate(id!).then(applyTemplateState).catch(() => {
          // The normal focus refetch remains the recovery path for a transient failure.
        });
      },
    }
  );

  const usedListIds = useMemo(
    () => new Set(sections.filter((s): s is DraftListSection => s.type === "list").map((s) => s.listId)),
    [sections]
  );

  const knownListIds = useMemo(() => new Set(lists.map((list) => list.id)), [lists]);

  const pickerLists = useMemo(() => {
    const q = pickerFilter.trim().toLowerCase();
    return lists.filter((l) => {
      if (usedListIds.has(l.id)) return false;
      if (!q) return true;
      if (l.name.toLowerCase().includes(q)) return true;
      if (l.description?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [lists, pickerFilter, usedListIds]);

  function addListSection(list: ApiListSummary) {
    if (sections.length >= LIMITS.templateSectionsMax) {
      showErrorToast(`Templates can have at most ${LIMITS.templateSectionsMax} sections.`);
      setPickerOpen(false);
      return;
    }
    const inherited = inheritedTemplateKind({
      isNew,
      kindTouched: kindTouchedRef.current,
      hasListSection: sections.some((s) => s.type === "list"),
      currentKind: kind,
      listKind: normalizeListKind(list.kind),
    });
    if (inherited) {
      setKind(inherited);
      showSuccessToast(`Kind set to To-do to match "${list.name}".`);
    }
    setSections((prev) => [
      ...prev,
      { key: newKey(), type: "list", listId: list.id, name: list.name },
    ]);
    setPickerOpen(false);
    setPickerFilter("");
  }

  async function openPdf() {
    const ids = [
      ...new Set(
        sections
          .filter((s): s is DraftListSection => s.type === "list")
          .map((s) => s.listId)
      ),
    ];
    try {
      const docs = (
        await Promise.all(ids.map((listId) => fetchList(listId).catch(() => null)))
      ).filter((doc): doc is ApiList => doc !== null);
      setPdfLists(docs);
    } catch {
      setPdfLists([]);
    }
    setPdfOpen(true);
  }

  function addInlineSection() {
    if (sections.length >= LIMITS.templateSectionsMax) {
      showErrorToast(`Templates can have at most ${LIMITS.templateSectionsMax} sections.`);
      return;
    }
    setSections((prev) => [
      ...prev,
      { key: newKey(), type: "inline", name: "Extras", items: [] },
    ]);
  }

  function removeSection(key: string) {
    setSections((prev) => prev.filter((s) => s.key !== key));
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeType = active.data.current?.type as string | undefined;

    if (activeType === "section") {
      setSections((prev) => {
        const oldIndex = prev.findIndex((s) => `tsection:${s.key}` === String(active.id));
        if (oldIndex < 0) return prev;

        const overType = over.data.current?.type as string | undefined;
        let newIndex = -1;
        if (overType === "section") {
          newIndex = prev.findIndex((s) => `tsection:${s.key}` === String(over.id));
        } else if (overType === "item") {
          const containerId = over.data.current?.containerId as string | undefined;
          if (containerId) {
            newIndex = prev.findIndex((s) => s.key === containerId);
          }
        }
        if (newIndex < 0 || newIndex === oldIndex) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
      return;
    }

    if (activeType !== "item") return;

    const overType = over.data.current?.type as string | undefined;
    const activeContainer = active.data.current?.containerId as string | undefined;
    const overContainer = over.data.current?.containerId as string | undefined;
    if (overType !== "item" || !activeContainer || activeContainer !== overContainer) return;

    setSections((prev) =>
      prev.map((s) => {
        if (s.key !== activeContainer || s.type !== "inline") return s;
        const oldIndex = s.items.findIndex((it) => it.id === active.id);
        const newIndex = s.items.findIndex((it) => it.id === over.id);
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return s;
        return { ...s, items: arrayMove(s.items, oldIndex, newIndex) };
      })
    );
  }

  function updateInlineName(key: string, value: string) {
    setSections((prev) =>
      prev.map((s) => (s.key === key && s.type === "inline" ? { ...s, name: value } : s))
    );
  }

  function addInlineItem(key: string, text: string) {
    const cleaned = text.trim().replace(/\s+/g, " ");
    if (!cleaned) return;
    const section = sections.find((s): s is DraftInlineSection => s.key === key && s.type === "inline");
    if (section && section.items.length >= LIMITS.listItemsMax) {
      showErrorToast(`Inline sections can have at most ${LIMITS.listItemsMax} items.`);
      return;
    }
    setSections((prev) =>
      prev.map((s) => {
        if (s.key !== key || s.type !== "inline") return s;
        if (s.items.length >= LIMITS.listItemsMax) return s;
        return {
          ...s,
          items: [...s.items, { id: crypto.randomUUID(), text: cleaned }],
        };
      })
    );
  }

  function importInlineItems(key: string, preview: string[]) {
    const section = sections.find((s): s is DraftInlineSection => s.key === key && s.type === "inline");
    if (!section) return;
    const remaining = LIMITS.listItemsMax - section.items.length;
    const { toAdd, skippedDuplicate, skippedLimit } = selectImportItems(
      preview,
      section.items.map((it) => it.text),
      remaining
    );
    if (toAdd.length === 0) {
      showSuccessToast(importResultMessage(0, skippedDuplicate, skippedLimit));
      return;
    }
    setSections((prev) =>
      prev.map((s) => {
        if (s.key !== key || s.type !== "inline") return s;
        return {
          ...s,
          items: [
            ...s.items,
            ...toAdd.map((text) => ({ id: crypto.randomUUID(), text })),
          ],
        };
      })
    );
    showSuccessToast(importResultMessage(toAdd.length, skippedDuplicate, skippedLimit));
  }

  function sortInlineItems(key: string, dir: SortDir) {
    setSections((prev) =>
      prev.map((s) => {
        if (s.key !== key || s.type !== "inline") return s;
        return { ...s, items: sortItemsByText(s.items, dir) };
      })
    );
  }

  function updateInlineItem(sectionKey: string, itemId: string, text: string) {
    setSections((prev) =>
      prev.map((s) => {
        if (s.key !== sectionKey || s.type !== "inline") return s;
        return {
          ...s,
          items: s.items.map((it) => (it.id === itemId ? { ...it, text } : it)),
        };
      })
    );
  }

  function removeInlineItem(sectionKey: string, itemId: string) {
    setSections((prev) =>
      prev.map((s) => {
        if (s.key !== sectionKey || s.type !== "inline") return s;
        return { ...s, items: s.items.filter((it) => it.id !== itemId) };
      })
    );
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const body = {
        name,
        description: description.trim() || undefined,
        tags: parseTags(),
        kind,
        sections: toWriteSections(sections),
        ...(!isNew ? { version } : {}),
      };
      if (isNew) {
        const created = await createTemplate(body);
        draftMirror.discard();
        setPendingDraft(null);
        showSuccessToast("Template created.");
        navigate(`/templates/${created.id}`, { replace: true });
      } else {
        const updated = await updateTemplate(id!, body);
        applyTemplateState(updated);
        draftMirror.discard();
        setPendingDraft(null);
        showSuccessToast("Template saved.");
      }
    } catch (err) {
      reportActionError(err, "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (isNew) return;
    const ok = await confirm({
      title: `Delete "${savedValue.name || "this template"}"?`,
      message: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteTemplate(id!);
      navigate("/templates", { replace: true });
    } catch (err) {
      reportActionError(err, "Failed to delete");
      setDeleting(false);
    }
  }

  function openCreateChecklist() {
    setCreateName(suggestedChecklistName(name));
    setCreateOpen(true);
    setCreateError(null);
  }

  async function onCreateChecklist(e: FormEvent) {
    e.preventDefault();
    if (isNew) return;
    setInstantiating(true);
    setCreateError(null);
    try {
      const result = await instantiateTemplate(id!, createName);
      const warning = describeCreateWarnings(result.warnings);
      if (warning) showErrorToast(warning);
      navigate(`/checklists/${result.checklist.id}`, { replace: true });
    } catch (err) {
      const message = humanizeApiError(err, "Failed to create checklist");
      showErrorToast(message);
      setCreateError(message);
      setInstantiating(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center text-sm text-muted">Loading…</div>
    );
  }

  if (notFound) return <ResourceNotFound kind="template" />;

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:py-10">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <GuardedLink
            to="/templates"
            className="text-sm font-medium text-brand-700 no-underline hover:underline"
          >
            ← My Templates
          </GuardedLink>
          <h1 className="mt-2 text-2xl font-semibold text-ink">
            {isNew ? "New template" : "Edit template"}
          </h1>
        </div>
        {!isNew ? (
          <div className="ml-auto flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              size="sm"
              disabled={saving || deleting || instantiating}
              onClick={openCreateChecklist}
            >
              Create checklist
            </Button>
            <Button type="button" variant="secondary" size="icon" aria-label="Download PDF" title="Download PDF" onClick={() => void openPdf()}>
              <Download aria-hidden="true" size={20} />
            </Button>
            {isOwner ? (
              <Button type="button" variant="secondary" size="icon" aria-label="Share template" title="Share template" onClick={() => setShareOpen(true)}>
                <Share2 aria-hidden="true" size={20} />
              </Button>
            ) : null}
            {isOwner ? <Button
              type="button"
              variant="secondary"
              size="icon"
              className="text-red-700"
              aria-label="Delete template"
              title="Delete template"
              disabled={deleting || saving}
              onClick={() => void onDelete()}
            >
              <Trash2 aria-hidden="true" size={20} />
            </Button> : null}
          </div>
        ) : null}
      </div>

      {readOnly ? <p className="mb-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-muted">You have view-only access to this template.</p> : null}
      {!readOnly && pendingDraft ? (
        <div className="mb-5">
          <DraftRestoreBar
            onRestore={() => {
              applyDraftValue(pendingDraft);
              setPendingDraft(null);
            }}
            onDiscard={() => {
              clearDraft(mirrorKey);
              setPendingDraft(null);
            }}
          />
        </div>
      ) : null}
      <form id={formId} onSubmit={(e) => void onSave(e)} className="space-y-5">
        <fieldset disabled={readOnly} className="space-y-5 disabled:opacity-75">
        <ErrorBanner message={error} />
        {error?.includes("Someone else changed") ? <Button type="button" variant="secondary" size="sm" onClick={() => window.location.reload()}>Reload</Button> : null}

        <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm sm:p-6">
          <label htmlFor="templateName" className={labelCls}>
            Name
          </label>
          <input
            id="templateName"
            className={fieldCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={LIMITS.nameMax}
            placeholder="e.g. Camping"
          />
          <CharCount value={name} max={LIMITS.nameMax} />
          <DescriptionHint show={!description.trim()} />
          <div className="mt-4">
            <label htmlFor="templateDesc" className={labelCls}>
              Description
            </label>
            <textarea
              id="templateDesc"
              className={`${fieldCls} min-h-[4.5rem] resize-y`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              placeholder="Optional notes about this template"
            />
            <CharCount value={description} max={500} />
          </div>
          <div className="mt-4">
            <label htmlFor="templateTags" className={labelCls}>
              Tags
            </label>
            <input
              id="templateTags"
              className={fieldCls}
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              placeholder="comma-separated, e.g. camping, kids"
            />
            <p className="mt-1 text-xs text-muted">Tags are saved in lowercase.</p>
          </div>
          <div className="mt-4">
            <p className={labelCls} id="templateKindLabel">
              Kind
            </p>
            <div
              className="mt-1 flex flex-wrap gap-2"
              role="group"
              aria-labelledby="templateKindLabel"
            >
              <button
                type="button"
                className={kindToggleCls(kind === "packing")}
                aria-pressed={kind === "packing"}
                onClick={() => {
                  kindTouchedRef.current = true;
                  setKind("packing");
                }}
              >
                Packing
              </button>
              <button
                type="button"
                className={kindToggleCls(kind === "todo")}
                aria-pressed={kind === "todo"}
                onClick={() => {
                  kindTouchedRef.current = true;
                  setKind("todo");
                }}
              >
                To-do
              </button>
            </div>
            <p className="mt-2 text-xs text-muted-sm">
              Copied onto checklists created from this template. You can change it on the checklist
              later.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-ink">
              Sections{" "}
              <span className="font-normal text-muted">
                ({sections.length}/{LIMITS.templateSectionsMax})
              </span>
            </h2>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setPickerFilter("");
                  setPickerOpen(true);
                }}
              >
                Add list
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={addInlineSection}>
                Add inline
              </Button>
            </div>
          </div>

          {sections.length === 0 ? (
            <p className="mt-4 text-sm text-muted">
              Add sections from your lists, or an inline section for one-off items.
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableList
                items={sections.map((s) => `tsection:${s.key}`)}
                disabled={readOnly}
                className="mt-4 flex flex-col gap-2"
              >
                {sections.map((section) => (
                  <SortableRow
                    key={section.key}
                    id={`tsection:${section.key}`}
                    disabled={readOnly}
                    data={{ type: "section" }}
                  >
                    {({ dragHandleProps, isDragging, setNodeRef, style }) => (
                      <div
                        ref={setNodeRef}
                        style={style}
                        className={[
                          "rounded-2xl border border-line bg-white p-3 sm:p-4",
                          isDragging ? "opacity-60 shadow-md" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <div className="flex items-start gap-2">
                          {!readOnly ? (
                            <DragHandle
                              {...dragHandleProps}
                              label="Drag to reorder section"
                              disabled={readOnly}
                            />
                          ) : null}
                          <div className="min-w-0 flex-1">
                            {section.type === "list" ? (
                              <div>
                                {(() => {
                                  const orphaned = isOrphanedListSection(
                                    isOwner,
                                    section.listId,
                                    knownListIds
                                  );
                                  const summary = lists.find((l) => l.id === section.listId);
                                  return (
                                    <>
                                      <p className="text-xs font-medium uppercase tracking-wide text-brand-700">
                                        List
                                        {orphaned ? (
                                          <span className="ml-2 font-semibold normal-case tracking-normal text-danger">
                                            List deleted
                                          </span>
                                        ) : null}
                                      </p>
                                      <p className="mt-0.5 text-sm font-semibold text-ink">
                                        {section.name}
                                        {summary ? (
                                          <span className="ml-2 font-normal text-muted">
                                            {summary.itemCount} item{summary.itemCount === 1 ? "" : "s"}
                                          </span>
                                        ) : null}
                                      </p>
                                      <p className="mt-1 text-xs text-muted-sm">
                                        {orphaned
                                          ? "This section will be skipped. Remove it or add another list."
                                          : "Each new checklist copies this list's current items. Existing checklists don't change."}
                                      </p>
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <InlineSectionEditor
                                section={section}
                                readOnly={readOnly}
                                onNameChange={(v) => updateInlineName(section.key, v)}
                                onAddItem={(text) => addInlineItem(section.key, text)}
                                onUpdateItem={(itemId, text) =>
                                  updateInlineItem(section.key, itemId, text)
                                }
                                onRemoveItem={(itemId) =>
                                  removeInlineItem(section.key, itemId)
                                }
                                onImportItems={(preview) =>
                                  importInlineItems(section.key, preview)
                                }
                                onSort={(dir) => sortInlineItems(section.key, dir)}
                              />
                            )}
                          </div>
                          {!readOnly ? (
                            <RemoveRowButton
                              onClick={() => removeSection(section.key)}
                            />
                          ) : null}
                        </div>
                      </div>
                    )}
                  </SortableRow>
                ))}
              </SortableList>
            </DndContext>
          )}
        </div>

        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : isNew ? "Create template" : "Save changes"}
        </Button>
        </fieldset>
      </form>

      <PdfExportDialog
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        docTitle={name}
        supportsProgress={false}
        supportsAttribution={false}
        shareResource={id ? { kind: "template", id, isOwner } : undefined}
        buildDoc={(_options, qrUrl) =>
          templateToPdfDoc(
            {
              name,
              kind,
              sections: toWriteSections(sections),
              url: qrUrl,
            },
            pdfLists
          )
        }
      />
      {!isNew && id ? (
        <SharingPanel
          resourceKind="template"
          resourceId={id}
          resourceName={name}
          isOwner={isOwner}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      ) : null}

      <Modal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        labelledBy="listPickerTitle"
      >
        <h2 id="listPickerTitle" className="text-lg font-semibold text-ink">
          Add a list section
        </h2>
        <p className="mt-1 text-sm text-muted">
          Pick from your lists. Templates only reference lists you own.
        </p>
        <input
          className={`${fieldCls} mt-4`}
          placeholder="Search lists…"
          value={pickerFilter}
          onChange={(e) => setPickerFilter(e.target.value)}
          autoFocus
        />
        {sections.length >= LIMITS.templateSectionsMax ? (
          <p className="mt-4 text-sm text-danger">
            Templates can have at most {LIMITS.templateSectionsMax} sections.
          </p>
        ) : null}
        {lists.length === 0 ? (
          <p className="mt-4 text-sm text-muted">
            No lists yet.{" "}
            <Link to="/lists/new" className="font-medium text-brand-700 hover:underline">
              Create a list
            </Link>{" "}
            first.
          </p>
        ) : pickerLists.length === 0 ? (
          <p className="mt-4 text-sm text-muted">No available lists match.</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {pickerLists.map((list) => (
              <li key={list.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg border border-line bg-white px-3 py-3 text-left transition hover:border-brand-200 hover:bg-brand-50/40 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-line disabled:hover:bg-white"
                  disabled={sections.length >= LIMITS.templateSectionsMax}
                  onClick={() => addListSection(list)}
                >
                  <span>
                    <span className="block text-sm font-semibold text-ink">{list.name}</span>
                    <span className="mt-0.5 block text-xs text-muted-sm">
                      {list.itemCount} item{list.itemCount === 1 ? "" : "s"}
                      {normalizeListKind(list.kind) === "todo" ? " · To-do" : " · Packing"}
                    </span>
                  </span>
                  <span className="text-sm font-medium text-brand-700">Add</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <Button
          type="button"
          variant="secondary"
          fullWidth
          className="mt-5"
          onClick={() => setPickerOpen(false)}
        >
          Cancel
        </Button>
      </Modal>

      <Modal
        open={createOpen}
        onClose={() => {
          if (!instantiating) setCreateOpen(false);
        }}
        labelledBy="createChecklistTitle"
      >
        <form onSubmit={(e) => void onCreateChecklist(e)}>
          <h2 id="createChecklistTitle" className="text-lg font-semibold text-ink">
            Create checklist from this
          </h2>
          <p className="mt-1 text-sm text-muted">
            Each list section is copied as it is now. Later edits to those lists won&apos;t change this checklist.
          </p>
          <div className="mt-3">
            <ErrorBanner message={createError} />
          </div>
          <label htmlFor="checklistName" className={`${labelCls} mt-4`}>
            Checklist name
          </label>
          <input
            id="checklistName"
            className={fieldCls}
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            required
            maxLength={LIMITS.nameMax}
            autoFocus
          />
          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <Button type="submit" fullWidth className="sm:w-auto" disabled={instantiating}>
              {instantiating ? "Creating…" : "Create checklist"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              fullWidth
              className="sm:w-auto"
              disabled={instantiating}
              onClick={() => setCreateOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function InlineSectionEditor({
  section,
  readOnly,
  onNameChange,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  onImportItems,
  onSort,
}: {
  section: DraftInlineSection;
  readOnly: boolean;
  onNameChange: (name: string) => void;
  onAddItem: (text: string) => void;
  onUpdateItem: (itemId: string, text: string) => void;
  onRemoveItem: (itemId: string) => void;
  onImportItems: (preview: string[]) => void;
  onSort: (dir: SortDir) => void;
}) {
  const [draft, setDraft] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-sm">Inline</p>
        <div className="flex flex-wrap gap-1.5">
          <CopyItemsButton items={section.items} />
          {!readOnly ? <ItemSortButtons items={section.items} onSort={onSort} /> : null}
        </div>
      </div>
      <input
        className={`${fieldCls} mt-1`}
        value={section.name}
        onChange={(e) => onNameChange(e.target.value)}
        maxLength={120}
        placeholder="Section name"
        aria-label="Inline section name"
      />
      {section.items.length > 0 ? (
        <SortableList
          items={section.items.map((it) => it.id)}
          disabled={readOnly}
          className="mt-3 space-y-2"
        >
          <ul className="space-y-2">
            {section.items.map((item, index) => (
              <SortableRow
                key={item.id}
                id={item.id}
                disabled={readOnly}
                data={{ type: "item", containerId: section.key }}
              >
                {({ dragHandleProps, isDragging, setNodeRef, style }) => (
                  <li
                    ref={setNodeRef}
                    style={style}
                    className={[
                      "flex items-center gap-1 rounded-lg border border-line bg-white px-2 py-1",
                      isDragging ? "opacity-60 shadow-md" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {!readOnly ? (
                      <DragHandle {...dragHandleProps} disabled={readOnly} />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <input
                        className="w-full rounded-lg border border-transparent bg-transparent px-1 py-1.5 text-sm text-ink outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20"
                        value={item.text}
                        maxLength={LIMITS.itemTextMax}
                        onChange={(e) => onUpdateItem(item.id, e.target.value)}
                        aria-label={`Item ${index + 1}`}
                      />
                      {item.text.length >= LIMITS.itemTextMax ? (
                        <p className="px-1 text-xs text-danger">
                          Items can be up to {LIMITS.itemTextMax} characters.
                        </p>
                      ) : null}
                    </div>
                    {!readOnly ? (
                      <RemoveRowButton onClick={() => onRemoveItem(item.id)} />
                    ) : null}
                  </li>
                )}
              </SortableRow>
            ))}
          </ul>
        </SortableList>
      ) : (
        <p className="mt-2 text-xs text-muted-sm">No items yet.</p>
      )}
      <div className="mt-2">
        <div className="flex flex-wrap gap-2">
        <input
          className={inlineFieldCls}
          value={draft}
          maxLength={LIMITS.itemTextMax}
          placeholder="Add an item…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAddItem(draft);
              setDraft("");
            }
          }}
        />
        <Button
          type="button"
          variant="secondary"
          className="shrink-0"
          onClick={() => {
            onAddItem(draft);
            setDraft("");
          }}
        >
          Add
        </Button>
        {!readOnly ? (
          <Button
            type="button"
            variant="secondary"
            className="shrink-0"
            onClick={() => setImportOpen(true)}
          >
            Import paste
          </Button>
        ) : null}
        </div>
        {draft.length >= LIMITS.itemTextMax ? (
          <p className="mt-1 text-xs text-danger">
            Items can be up to {LIMITS.itemTextMax} characters.
          </p>
        ) : null}
      </div>
      <ImportItemsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onConfirm={onImportItems}
        remaining={LIMITS.listItemsMax - section.items.length}
        description="Paste one item per line. Bullets, numbers, and the first cell of tab-separated lines are cleaned up. Items are added to this section."
      />
    </div>
  );
}
